/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Authentication API Service
 *
 * Handles all authentication-related API calls.
 * Can be used across any feature, component, or page.
 *
 * Note: All endpoints use full paths (e.g., /hub/v1/auth/...)
 */
import { apiClient } from '@/lib/axiosClient';
import { API_CONFIG } from '@/constants';
import type { ApiResponse } from './types';
import { LoginRequest, LoginResponse } from '@/features/auth/types';

// API prefix for auth endpoints
const AUTH_PREFIX = `${API_CONFIG.HUB_PREFIX}/auth`;

// Login result (user data and tokens)
export interface LoginResult {
  user: UserData;
  tokens: AuthTokens;
}

// User data structure
export interface UserData {
  id: string;
  userId: string;
  userName: string;
  email: string;
  companyCode: string;
  role: 'admin' | 'user' | 'manager';
  department?: string;
  phone?: string;
  isActive: boolean;
}

// Authentication tokens
export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

// ============================================================================
// API Service
// ============================================================================

/**
 * Authentication API endpoints
 */
export const authApi = {
  /**
   * Login with email and password
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      `${AUTH_PREFIX}/signin`,
      credentials,
    );
    return response.data;
  },

  /**
   * Logout current user
   * Backend reads userId from JWT token in HttpOnly cookie
   */
  logout: async (): Promise<void> => {
    await apiClient.post(`${AUTH_PREFIX}/signout`);
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser: async (): Promise<any> => {
    const response = await apiClient.get<any>(`${AUTH_PREFIX}/me`);
    return response.data;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (
    request: RefreshTokenRequest,
  ): Promise<ApiResponse<AuthTokens>> => {
    const response = await apiClient.post<ApiResponse<AuthTokens>>(
      `${AUTH_PREFIX}/refresh`,
      request,
    );
    return response.data;
  },

  /**
   * Initiate SSO login
   * Redirects to OneLogin for authentication
   */
  initiateSSO: async (): Promise<{ redirectUrl: string }> => {
    const response = await apiClient.get<{ redirectUrl: string }>(
      `${API_CONFIG.SSO_LOGIN_URL}`,
    );
    return response.data;
  },

  /**
   * SSO login callback
   * Handles SAML response from OneLogin
   *
   * @param SAMLResponse - SAML response from OneLogin (matches backend field name)
   * @param RelayState - Optional relay state (matches backend field name)
   */
  ssoCallback: async (data: {
    SAMLResponse: string;
    RelayState?: string;
  }): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      `${API_CONFIG.SSO_CALLBACK_URL}`,
      data,
    );
    return response.data;
  },

  /**
   * Request password reset
   * Sends password reset request to administrator
   *
   * @param companyCode - Company code
   * @param userId - User ID
   */
  forgotPassword: async (data: {
    companyCode: string;
    userId: string;
  }): Promise<ApiResponse<void>> => {
    const response = await apiClient.post<ApiResponse<void>>(
      `${AUTH_PREFIX}/forgotpassword`,
      data,
    );
    return response.data;
  },
};
